// Considered: `SphereTimelineEventMolecule` (the old Activity/Timeline row) — rejected; it renders
// a type label plus a raw property bag (Problem Statement), with no sentence grammar, no
// provenance, no delta, no fold. This composes only the atoms/molecules the Reuse Audit justifies.
// See spec Reuse Audit (specs/stream-trajectory-tools.md).
//
// Source: design/metaspace-tools/metaspace-tools-v2.jsx:147-224 (`MSEntry` usages inside
// `StreamPanelV2`). Sentence built by `buildStreamSentence` (pure, unit-tested separately).

import React from 'react';
import { useTranslation } from 'localization';
import type { StreamChord } from 'types/stream';
import type { InspectorPayload } from 'types/inspector';
import { buildStreamSentence } from '../../../helpers/stream/buildStreamSentence';
import { toDisplayValue } from '../../../helpers/stream/toDisplayValue';
import { resolveStreamProvenanceChips } from '../../../helpers/stream/resolveStreamProvenanceChip';
import {
  buildChordPayload,
  buildDerivationPayload,
  buildIdentityPayload,
  buildProvenancePayload,
  buildValidationPayload,
  buildValueHistoryPayload,
} from '../../../helpers/stream/buildStreamInspectorPayloads';
import InitialsAvatarAtom from '../../atoms/InitialsAvatar/InitialsAvatarAtom';
import DeltaRunAtom from '../../atoms/DeltaRun/DeltaRunAtom';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import InspectableMolecule from '../Inspectable/InspectableMolecule';
import ConsequenceFoldMolecule from '../ConsequenceFold/ConsequenceFoldMolecule';
import './StreamEntryMolecule.scss';

export interface StreamEntryMoleculeProps {
  chord: StreamChord;
  sphereId: string;
  /** Resolved display label for `chord.root.aspectSystemName`, when present. */
  aspectLabel: string;
  onInspect: (payload: InspectorPayload) => void;
  /** Fold state is PER ENTRY (this chord's correlationId), never per day-group. */
  folded: boolean;
  onToggleFold: () => void;
  /** Human-readable relative time, e.g. "4d" — pre-formatted by the panel so this stays pure. */
  relativeTime: string;
  onJumpToTrack?: (aspectSystemName: string) => void;
  className?: string;
}

const StreamEntryMolecule: React.FC<StreamEntryMoleculeProps> = ({
  chord,
  sphereId,
  aspectLabel,
  onInspect,
  folded,
  onToggleFold,
  relativeTime,
  onJumpToTrack,
  className,
}) => {
  const { t } = useTranslation();
  const { root } = chord;
  const sentence = buildStreamSentence(root, root.subject, t);
  const chips = resolveStreamProvenanceChips(root);
  const isAspectObservation = root.entryType === 'aspect_observation';

  const chordPayload = buildChordPayload(chord, sphereId, root.subject ? sentence : `${root.actorName} ${sentence}`, t);
  const identityPayload = buildIdentityPayload(root.actor, root.actorName, t);

  const chipInspectPayload = (chip: ReturnType<typeof resolveStreamProvenanceChips>[number]) => {
    if (chip.labelKey === 'stream.chips.needsValidation') return buildValidationPayload(root, aspectLabel, sphereId, t);
    if (chip.tone === 'calc') return buildDerivationPayload(root, aspectLabel, sphereId, t);
    return buildProvenancePayload(root, aspectLabel, sphereId, t);
  };

  const previousValue = root.previous ? Object.values(root.previous)[0] : null;
  const newValue = Object.values(root.content ?? {})[0];

  return (
    <div className={['stream-entry-molecule', className].filter(Boolean).join(' ')} data-testid="stream-entry">
      <div className="stream-entry-molecule__row">
        <InspectableMolecule payload={identityPayload} onInspect={onInspect} peek={root.actorName}>
          <span className="stream-entry-molecule__actor">
            <InitialsAvatarAtom name={root.actorName} />
            <strong>{root.actorName}</strong>
          </span>
        </InspectableMolecule>

        <div className="stream-entry-molecule__body">
          {isAspectObservation ? (
            <>
              {onJumpToTrack && root.aspectSystemName ? (
                <button
                  type="button"
                  className="stream-entry-molecule__aspect-link focus-ring"
                  onClick={() => onJumpToTrack(root.aspectSystemName as string)}
                >
                  <strong>{aspectLabel}</strong> <span className="stream-entry-molecule__muted">{sentence}</span>
                </button>
              ) : (
                <span>
                  <strong>{aspectLabel}</strong> <span className="stream-entry-molecule__muted">{sentence}</span>
                </span>
              )}
              <InspectableMolecule payload={buildValueHistoryPayload(root, aspectLabel, sphereId, t)} onInspect={onInspect}>
                <DeltaRunAtom
                  previous={previousValue != null ? toDisplayValue(previousValue) : null}
                  next={toDisplayValue(newValue)}
                />
              </InspectableMolecule>
              {chips.map((chip) => (
                <InspectableMolecule key={chip.labelKey} payload={chipInspectPayload(chip)} onInspect={onInspect}>
                  <ChipAtom icon={chip.icon} label={t(chip.labelKey)} tone={chip.tone} dashed={chip.dashed} />
                </InspectableMolecule>
              ))}
            </>
          ) : (
            <InspectableMolecule payload={chordPayload} onInspect={onInspect} block>
              <span>
                <span className="stream-entry-molecule__muted">{sentence}</span>{' '}
                {root.subject && <strong>{root.subject.name}</strong>}
              </span>
            </InspectableMolecule>
          )}
        </div>

        <span className="stream-entry-molecule__ago">{relativeTime}</span>
      </div>

      {/* `aspectUserAuthorable === false` (the aspect's `valuePolicy.automation` is `'auto'`)
          means the platform computes this value and nobody authors it — there is nothing for a
          responsible viewer to attest to, so the acts are HIDDEN, matching how the chip is
          suppressed in `resolveStreamProvenanceChips`. */}
      {isAspectObservation &&
        root.aspectUserAuthorable !== false &&
        root.validationStatus === 'unvalidated' &&
        root.viewerIsResponsible && (
        <div className="stream-entry-molecule__acts">
          <ButtonAtom
            shape="pill"
            size="small"
            variant="primary"
            icon="lucide:check"
            label={t('stream.acts.attest')}
            onClick={() => onInspect(buildValidationPayload(root, aspectLabel, sphereId, t))}
          />
          <ButtonAtom
            shape="pill"
            size="small"
            variant="secondary"
            label={t('stream.acts.reject')}
            onClick={() => onInspect(buildValidationPayload(root, aspectLabel, sphereId, t))}
          />
        </div>
      )}

      <ConsequenceFoldMolecule
        consequences={chord.consequences}
        folded={folded}
        onToggle={onToggleFold}
        buildPayload={(entry) => buildValueHistoryPayload(entry, aspectLabel, sphereId, t)}
        onInspect={onInspect}
      />
    </div>
  );
};

StreamEntryMolecule.displayName = 'StreamEntryMolecule';

export default StreamEntryMolecule;
